import { Slot, Slottable } from '@radix-ui/react-slot'
import * as React from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '../feedback/tooltip'
import { cn } from '../lib/cn'
import type { IconComponent } from '../lib/icons'

// The rows of the navigation and their headings. Why they read the collapsed state from a context,
// not a prop, and what collapsing must not move: packages/ui/docs/nessuna-stringa-qui-dentro.md

/** Provided by `AppShell` twice over: `false` around the panel, the real value around the
 *  column. The same `sidebar` node renders in both and must answer differently. */
export const CollapsedContext = React.createContext(false)

export interface AppShellNavGroupProps {
  /** The heading over the rows. Absent for a run of links that needs none: how many rows earn
   *  one is the app's decision, the look of one is this package's. */
  title?: string
  children: React.ReactNode
}

/** A run of nav links under an optional heading. */
export function AppShellNavGroup({ title, children }: AppShellNavGroupProps) {
  const collapsed = React.useContext(CollapsedContext)

  // One element in both states, and the air above the words rather than around them. What
  // two elements cost: packages/ui/docs/nessuna-stringa-qui-dentro.md.
  function heading() {
    if (title === undefined) return null
    return (
      <p
        className={cn(
          'flex h-9 px-3 text-xs font-medium text-muted-foreground',
          collapsed ? 'items-center' : 'items-end pb-1',
        )}
      >
        {/* Collapsed the words are unreadable at 64px, but a nameless run of ten rows is
            worse: they go `sr-only` and a rule stands where they were. */}
        {collapsed ? (
          <>
            <span className="sr-only">{title}</span>
            <span aria-hidden="true" className="h-px w-full bg-border" />
          </>
        ) : (
          title
        )}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      {heading()}
      {children}
    </div>
  )
}

export interface AppShellNavLinkProps extends React.ComponentPropsWithoutRef<'a'> {
  /** The words. A prop and not `children` because the row hides them when the column is
   *  collapsed and feeds them to the tooltip — the same inversion `CardListText` makes. */
  label: React.ReactNode
  /** The current item: marked `aria-current` for screen readers, not merely coloured. */
  active?: boolean
  /** Renders the child instead of its own `<a>`, with the styles and `aria-current`. The
   *  inversion lives here because TanStack Router's `Link` accepts no `asChild`. */
  asChild?: boolean
  /** Decorative icon before the words; rendered `aria-hidden` because the label already
   *  names the destination. Without one a collapsed row is a blank square. */
  icon?: IconComponent
}

/** A sidebar navigation item. A link is an `<a>`, not a button. `min-h-11` is the thumb target: the
 *  panel below `lg` is the primary surface, see packages/ui/docs/nessuna-stringa-qui-dentro.md. */
export const AppShellNavLink = React.forwardRef<HTMLAnchorElement, AppShellNavLinkProps>(
  ({ className, label, active = false, asChild = false, icon: Icon, children, ...props }, ref) => {
    const collapsed = React.useContext(CollapsedContext)
    const Component = asChild ? Slot : 'a'
    const glyph =
      Icon === undefined ? null : <Icon aria-hidden="true" className="size-4 shrink-0" />
    const style = cn(
      'flex min-h-11 items-center rounded-pill text-sm font-medium transition-colors focus-visible:outline-none focus-visible:focus-volee',
      collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2',
      // The one place the accent is spent: a full lime pill. Hover is a different fill from
      // it, or a hovered row and the page you are on look alike.
      active
        ? 'bg-primary text-primary-foreground [&>svg]:text-primary-foreground'
        : 'text-foreground/80 hover:bg-secondary hover:text-foreground',
      className,
    )
    // `sr-only` and not removed: the row keeps its accessible name, so a screen reader reads
    // the same string whether or not the column is showing it.
    const words = collapsed ? <span className="sr-only">{label}</span> : label

    const row = (
      // Outside a `Slot`, `Slottable` renders its children — so the icon and the words land
      // inside the router's own link with `asChild`, and inside this `<a>` without it.
      <Component ref={ref} aria-current={active ? 'page' : undefined} className={style} {...props}>
        <Slottable>{children}</Slottable>
        {glyph}
        {words}
      </Component>
    )

    if (!collapsed) return row

    return (
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    )
  },
)
AppShellNavLink.displayName = 'AppShellNavLink'
